import { API_BASE_URL } from '../config/api';

export type NotificationItem = {
  notification_id: string;
  practice_id: string;
  type: string;
  title: string;
  body: string;
  link_url: string;
  created_at_ms: number;
  is_read: boolean;
  read_at_ms?: number | null;
  image_url?: string | null;
  meta?: Record<string, unknown> | null;
};

export type ListNotificationsResponse = { items: NotificationItem[] };
export type UnreadCountResponse = { unread_count: number };
export type NotificationPreferences = { enabled_types: Record<string, boolean> };

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  if (!token) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  return response.json();
};

export const listNotifications = async (params: {
  scope?: 'all' | 'unread';
  limit?: number;
  before?: number;
} = {}): Promise<ListNotificationsResponse> => {
  const url = new URL(`${API_BASE_URL}/api/v1/notifications`);
  url.searchParams.set('scope', params.scope || 'all');
  if (params.limit) url.searchParams.set('limit', String(params.limit));
  if (params.before) url.searchParams.set('before', String(params.before));

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<ListNotificationsResponse>(response);
};

export const getUnreadCount = async (): Promise<UnreadCountResponse> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/unread-count`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<UnreadCountResponse>(response);
};

export const markNotificationRead = async (notificationId: string): Promise<{ status: string }> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/${notificationId}/read`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({}),
  });
  return handleResponse<{ status: string }>(response);
};

export const readAllNotifications = async (limit = 200): Promise<{ status: string; marked_read: number }> => {
  const url = new URL(`${API_BASE_URL}/api/v1/notifications/read-all`);
  url.searchParams.set('limit', String(limit));
  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({}),
  });
  return handleResponse<{ status: string; marked_read: number }>(response);
};

export const getNotificationPreferences = async (): Promise<NotificationPreferences> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/preferences`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<NotificationPreferences>(response);
};

export const updateNotificationPreferences = async (
  enabledTypes: Record<string, boolean>,
): Promise<NotificationPreferences> => {
  const response = await fetch(`${API_BASE_URL}/api/v1/notifications/preferences`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ enabled_types: enabledTypes }),
  });
  return handleResponse<NotificationPreferences>(response);
};


